// Performance Monitoring Wrapper
// Safely handles deprecated Performance APIs and provides fallbacks

// Safe wrapper for performance.getEntriesByType
export const safeGetEntriesByType = (type) => {
  // In development mode, completely avoid deprecated APIs that cause warnings
  if (process.env.NODE_ENV === 'development' && (
    type === 'paint' || 
    type === 'largest-contentful-paint' ||
    type === 'longtask'
  )) {
    return [];
  }
  
  try {
    if (typeof performance !== 'undefined' && performance.getEntriesByType) {
      const entries = performance.getEntriesByType(type);
      return entries;
    }
    return [];
  } catch (error) {
    // Suppress warnings in development mode
    if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
      console.warn(`Failed to get entries for type: ${type}`, error);
    }
    return [];
  }
};

// Safe wrapper for performance.getEntries
export const safeGetEntries = () => {
  try {
    if (typeof performance !== 'undefined' && performance.getEntries) {
      return performance.getEntries();
    }
    return [];
  } catch (error) {
    // Suppress warnings in development mode
    if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
      console.warn('Failed to get performance entries', error);
    }
    return [];
  }
};

// Safe wrapper for performance.now
export const safePerformanceNow = () => {
  try {
    if (typeof performance !== 'undefined' && performance.now) {
      return performance.now();
    }
    return Date.now();
  } catch (error) {
    // Suppress warnings in development mode
    if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
      console.warn('Performance.now failed, falling back to Date.now', error);
    }
    return Date.now();
  }
};

// Safe wrapper for memory metrics
export const safeGetMemoryMetrics = () => {
  try {
    if (typeof performance !== 'undefined' && performance.memory) {
      return {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
        usage: (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit * 100).toFixed(2) + '%'
      };
    }
    return null;
  } catch (error) {
    // Suppress warnings in development mode
    if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
      console.warn('Memory metrics not available', error);
    }
    return null;
  }
};

// Safe wrapper for navigation timing
export const safeGetNavigationTiming = () => {
  try {
    const navigation = safeGetEntriesByType('navigation')[0];
    if (navigation) {
      return {
        loadEventEnd: navigation.loadEventEnd || 0,
        loadEventStart: navigation.loadEventStart || 0,
        domInteractive: navigation.domInteractive || 0,
        navigationStart: navigation.navigationStart || 0
      };
    }
    return {
      loadEventEnd: 0,
      loadEventStart: 0,
      domInteractive: 0,
      navigationStart: 0
    };
  } catch (error) {
    // Suppress warnings in development mode
    if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
      console.warn('Navigation timing failed', error);
    }
    return {
      loadEventEnd: 0,
      loadEventStart: 0,
      domInteractive: 0,
      navigationStart: 0
    };
  }
};

// Safe wrapper for paint timing
export const safeGetPaintTiming = () => {
  // In development mode, return mock values to avoid deprecated API warnings
  if (process.env.NODE_ENV === 'development') {
    return { fcp: 0, lcp: 0 };
  }
  
  try {
    const paint = safeGetEntriesByType('paint');
    const fcp = paint.find(entry => entry.name === 'first-contentful-paint');
    const lcp = safeGetEntriesByType('largest-contentful-paint');
    
    return {
      fcp: fcp?.startTime || 0,
      lcp: lcp[lcp.length - 1]?.startTime || 0
    };
  } catch (error) {
    // Suppress warnings in development mode
    if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
      console.warn('Paint timing failed', error);
    }
    return { fcp: 0, lcp: 0 };
  }
};

// Safe wrapper for resource timing
export const safeGetResourceTiming = () => {
  try {
    return safeGetEntriesByType('resource');
  } catch (error) {
    // Suppress warnings in development mode
    if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
      console.warn('Resource timing failed', error);
    }
    return [];
  }
};

// Safe wrapper for long task timing
export const safeGetLongTaskTiming = () => {
  // In development mode, return empty array to avoid deprecated API warnings
  if (process.env.NODE_ENV === 'development') {
    return [];
  }
  
  try {
    return safeGetEntriesByType('longtask');
  } catch (error) {
    // Suppress warnings in development mode
    if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
      console.warn('Long task timing failed', error);
    }
    return [];
  }
};
